import { buildApiUrl } from './config';

interface PortalBranding {
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
}

interface EmailSubmissionSettings {
  isEnabled: boolean;
  emailAddress?: string;
}

interface PortalSettings {
  branding: PortalBranding;
  emailSubmission?: EmailSubmissionSettings;
  isKnowledgeBasePublic?: boolean;
}

export interface OrganizationInfo {
  organizationId: string;
  name: string;
  subdomain: string;
  isActive: boolean;
  portalSettings: PortalSettings;
}

export interface PortalLoaderData {
  organizationInfo: OrganizationInfo;
  orgSlug: string;
  isPathBased: boolean;
}

// Type definition for portal context stored on window
interface PortalContext {
  organizationInfo?: {
    organizationId: string;
    organizationSlug: string;
    organizationName: string;
  };
}

// Extend window interface to include portal context
declare global {
  interface Window {
    __PORTAL_CONTEXT__?: PortalContext;
  }
}

// Cache for validateOrganization to prevent redundant API calls
interface CacheEntry {
  data: OrganizationInfo;
  timestamp: number;
}

const organizationCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Clear organization cache - call on logout
export const clearOrganizationCache = (): void => {
  organizationCache.clear();
};

export const validateOrganization = async (
  orgSlug: string
): Promise<OrganizationInfo> => {
  // Check cache first
  const cached = organizationCache.get(orgSlug);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  // The route's error page shows this Error's message, so keep fetch/JSON parser text ("Failed to fetch") out of it
  let response: Response;
  try {
    response = await fetch(buildApiUrl(`/organization-portal/validate/${orgSlug}`));
  } catch {
    throw new Error("Unable to reach the server. Check your connection and try again.");
  }
  const result = await response.json().catch(() => null);

  if (!response.ok || !result?.success) {
    throw new Error(
      result?.message || "Unable to load this organization's portal. Please try again."
    );
  }

  // Cache the successful result
  organizationCache.set(orgSlug, {
    data: result.data,
    timestamp: Date.now(),
  });

  return result.data;
};

export const validateHelpdeskAccess = async (
  orgSlug: string
): Promise<OrganizationInfo> => {
  // First, validate the organization exists and is active
  const response = await fetch(buildApiUrl(`/organization-portal/validate/${orgSlug}`));
  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Organization not found");
  }

  const organizationInfo = result.data;

  // Check if user has admin/agent token
  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    throw new Error("You must be logged in to access the helpdesk");
  }

  // Decode JWT to check if user has admin/agent access
  try {
    const payload = JSON.parse(atob(authToken.split(".")[1]));

    // Check if user has a role (customers don't have roles in JWT)
    const userRole =
      payload.role ||
      payload[Object.keys(payload).find((key) => key.includes("role")) || ""];
    if (!userRole || !["Admin", "Agent"].includes(userRole)) {
      throw new Error("You must be an admin or agent to access the helpdesk");
    }

    const currentOrgId = payload.organization_id;

    // If user's current organization matches requested organization, they have access
    if (currentOrgId === organizationInfo.organizationId) {
      console.log("User validated for helpdesk access:", {
        userRole,
        requestedOrg: organizationInfo.organizationId,
        currentOrg: currentOrgId,
        status: "direct_access",
      });
      return organizationInfo;
    }

    // If organizations don't match, we need to verify if user has access to requested org
    // We can't verify this client-side, so we'll need to attempt the switch and handle failure
    console.log("User attempting organization switch for helpdesk access:", {
      userRole,
      requestedOrg: organizationInfo.organizationId,
      currentOrg: currentOrgId,
      status: "attempting_switch",
    });
  } catch (error) {
    if (
      error instanceof Error &&
      (error.message.includes("access") ||
        error.message.includes("admin") ||
        error.message.includes("agent"))
    ) {
      throw error;
    }
    throw new Error("Invalid authentication token");
  }

  return organizationInfo;
};

export const validateAdminAccess = async (
  orgSlug: string
): Promise<OrganizationInfo> => {
  // First, validate the organization exists and is active
  const response = await fetch(buildApiUrl(`/organization-portal/validate/${orgSlug}`));
  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Organization not found");
  }

  const organizationInfo = result.data;

  // Check if user has admin token
  const authToken = localStorage.getItem("authToken");
  if (!authToken) {
    throw new Error("You must be logged in to access admin features");
  }

  // Decode JWT to check if user has admin access
  try {
    const payload = JSON.parse(atob(authToken.split(".")[1]));

    // Check if user has admin role specifically
    const userRole =
      payload.role ||
      payload[Object.keys(payload).find((key) => key.includes("role")) || ""];
    if (!userRole || userRole !== "Admin") {
      throw new Error("You must be an admin to access user management");
    }

    const currentOrgId = payload.organization_id;

    // If user's current organization matches requested organization, they have access
    if (currentOrgId === organizationInfo.organizationId) {
      console.log("Admin validated for user management access:", {
        userRole,
        requestedOrg: organizationInfo.organizationId,
        currentOrg: currentOrgId,
        status: "direct_access",
      });
      return organizationInfo;
    }

    // If organizations don't match, we need to verify if user has admin access to requested org
    console.log("Admin attempting organization switch for user management:", {
      userRole,
      requestedOrg: organizationInfo.organizationId,
      currentOrg: currentOrgId,
      status: "requires_switch",
    });
    return organizationInfo;
  } catch (error: unknown) {
    if (
      error instanceof Error &&
      (error.message.includes("access") ||
        error.message.includes("admin"))
    ) {
      throw error;
    }
    throw new Error("Invalid authentication token");
  }

  return organizationInfo;
};

export const extractOrgSlugFromPath = (pathname: string): string | null => {
  // Extract organization from path (e.g., /org/acme/portal)
  const pathMatch = pathname.match(/^\/org\/([^/]+)/);
  return pathMatch ? pathMatch[1] : null;
};

export const extractOrgSlugFromSubdomain = (
  hostname: string
): string | null => {
  // Try subdomain detection for /portal routes
  if (
    !hostname.includes("localhost") &&
    !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)
  ) {
    const parts = hostname.split(".");
    if (parts.length >= 3) {
      const subdomain = parts[0];
      if (!["www", "api", "admin", "app"].includes(subdomain.toLowerCase())) {
        return subdomain;
      }
    }
  }
  return null;
};

export const createOrgBasedLoader =
  () =>
  async ({ params }: { params: Record<string, string> }) => {
    const orgSlug = params.orgSlug as string;

    if (!orgSlug) {
      throw new Error("Organization slug is required");
    }

    const organizationInfo = await validateOrganization(orgSlug);

    // Clear any mismatched customer tokens and themes for this organization
    clearMismatchedCustomerTokens(organizationInfo.organizationId);
    clearMismatchedCustomerThemes(organizationInfo.organizationId);

    const loaderData = {
      organizationInfo,
      orgSlug,
      isPathBased: true,
    } as PortalLoaderData;

    // Set portal context immediately on window object before component mount
    // This ensures AuthContext can access organization info during initialization
    window.__PORTAL_CONTEXT__ = {
      organizationInfo: {
        organizationId: organizationInfo.organizationId,
        organizationSlug: organizationInfo.subdomain,
        organizationName: organizationInfo.name
      }
    };

    return loaderData;
  };

export const createSubdomainBasedLoader = () => async () => {
  const hostname = window.location.hostname;
  const orgSlug = extractOrgSlugFromSubdomain(hostname);

  if (!orgSlug) {
    throw new Error("Organization not specified in subdomain");
  }

  const organizationInfo = await validateOrganization(orgSlug);

  // Clear any mismatched customer tokens and themes for this organization
  clearMismatchedCustomerTokens(organizationInfo.organizationId);
  clearMismatchedCustomerThemes(organizationInfo.organizationId);

  const loaderData = {
    organizationInfo,
    orgSlug,
    isPathBased: false,
  } as PortalLoaderData;

  // Set portal context immediately on window object before component mount
  // This ensures AuthContext can access organization info during initialization
  window.__PORTAL_CONTEXT__ = {
    organizationInfo: {
      organizationId: organizationInfo.organizationId,
      organizationSlug: organizationInfo.subdomain,
      organizationName: organizationInfo.name
    }
  };

  return loaderData;
};

export const createHelpdeskOrgLoader =
  () =>
  async ({ params }: { params: Record<string, string> }) => {
    const orgSlug = params.orgSlug as string;

    if (!orgSlug) {
      throw new Error("Organization slug is required");
    }

    const organizationInfo = await validateHelpdeskAccess(orgSlug);

    // Auto-switch organization context if needed
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split(".")[1]));
        const currentOrgId = payload.organization_id;

        if (currentOrgId !== organizationInfo.organizationId) {
          console.log("Organization context mismatch - attempting to switch", {
            currentOrgId,
            requestedOrgId: organizationInfo.organizationId,
          });

          // Need to switch organization context
          const response = await fetch(buildApiUrl('/auth/switch-organization'), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              organizationId: organizationInfo.organizationId,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data?.token) {
              // Update token in localStorage
              localStorage.setItem("authToken", result.data.token);
              if (result.data.refreshToken) {
                localStorage.setItem("refreshToken", result.data.refreshToken);
              }
              console.log(
                "Successfully switched organization context - refreshing page to update state"
              );

              // Reload the page to update AuthContext with new organization data
              window.location.reload();
            } else {
              console.error(
                "Organization switch response missing token:",
                result
              );
            }
          } else {
            const errorResult = await response.json();
            console.error("Failed to switch organization:", {
              status: response.status,
              error: errorResult.message || errorResult.errors,
            });

            // If organization switch fails, throw error to prevent access
            throw new Error(
              `You do not have access to this organization's helpdesk.`
            );
          }
        }
      } catch (error) {
        console.error("Error switching organization context:", error);

        // Re-throw access-related errors to prevent unauthorized access
        if (
          error instanceof Error &&
          error.message.includes("do not have access")
        ) {
          throw error;
        }

        // For other errors, let the user proceed (network issues, etc.)
      }
    }

    return {
      organizationInfo,
      orgSlug,
      isPathBased: true,
    } as PortalLoaderData;
  };

export const checkCustomerAuthentication = (
  organizationId?: string
): boolean => {
  if (organizationId) {
    // Check for organization-specific token first
    const orgSpecificToken = localStorage.getItem(
      `customerToken_${organizationId}`
    );
    if (
      orgSpecificToken &&
      validateTokenOrganization(orgSpecificToken, organizationId)
    ) {
      return true;
    }

    // Fallback: check legacy customerToken and validate it matches organization
    const legacyToken = localStorage.getItem("customerToken");
    if (legacyToken) {
      if (validateTokenOrganization(legacyToken, organizationId)) {
        // Migrate legacy token to organization-specific key
        localStorage.setItem(`customerToken_${organizationId}`, legacyToken);
        localStorage.removeItem("customerToken");
        return true;
      } else {
        // Token is for wrong organization, remove it
        localStorage.removeItem("customerToken");
        return false;
      }
    }

    return false;
  }

  // If no organizationId provided, try to extract from any available tokens
  // First check if there's any organization-specific token
  const keys = Object.keys(localStorage);
  for (const key of keys) {
    if (key.startsWith("customerToken_")) {
      const token = localStorage.getItem(key);
      const orgId = key.replace("customerToken_", "");
      if (token && validateTokenOrganization(token, orgId)) {
        return true;
      }
    }
  }

  // Without organization context, we cannot validate the legacy token
  // Return false to force proper authentication
  return false;
};

const validateTokenOrganization = (
  token: string,
  expectedOrganizationId: string
): boolean => {
  try {
    // Decode JWT token (simple base64 decode, no signature verification needed for client-side)
    const payload = JSON.parse(atob(token.split(".")[1]));
    const tokenOrganizationId = payload.organization_id;
    return tokenOrganizationId === expectedOrganizationId;
  } catch (error) {
    console.error("Error validating token organization:", error);
    return false;
  }
};

export const clearMismatchedCustomerTokens = (
  currentOrganizationId: string
): void => {
  // Check and clear legacy token if it doesn't match
  const legacyToken = localStorage.getItem("customerToken");
  if (
    legacyToken &&
    !validateTokenOrganization(legacyToken, currentOrganizationId)
  ) {
    localStorage.removeItem("customerToken");
  }

  // Check and clear organization-specific tokens that don't match
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith("customerToken_")) {
      const token = localStorage.getItem(key);
      const orgId = key.replace("customerToken_", "");

      if (token && orgId !== currentOrganizationId) {
        // This token is for a different organization - validate it's still valid for that org
        if (!validateTokenOrganization(token, orgId)) {
          localStorage.removeItem(key);
        }
      }
    }
  });
};

export const clearMismatchedCustomerThemes = (
  currentOrganizationId: string
): void => {
  // Migrate legacy customerTheme to organization-specific key if it doesn't exist
  const legacyTheme = localStorage.getItem("customerTheme");
  const currentOrgTheme = localStorage.getItem(
    `customerTheme_${currentOrganizationId}`
  );

  if (legacyTheme && !currentOrgTheme) {
    // Migrate legacy theme to current organization
    localStorage.setItem(`customerTheme_${currentOrganizationId}`, legacyTheme);
  }

  // Clear legacy theme after migration
  if (legacyTheme) {
    localStorage.removeItem("customerTheme");
  }
};

export const clearAllCustomerThemes = (): void => {
  // Clear legacy theme
  localStorage.removeItem("customerTheme");

  // Clear all organization-specific themes
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith("customerTheme_")) {
      localStorage.removeItem(key);
    }
  });
};

export const clearCustomerDataForLogout = (): void => {
  // Clear all customer tokens
  localStorage.removeItem("customerToken");
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith("customerToken_")) {
      localStorage.removeItem(key);
    }
    // Clear customer portal ticket filters
    if (key.startsWith("customer-portal-tickets-filters-")) {
      localStorage.removeItem(key);
    }
  });

  // Clear all customer themes
  clearAllCustomerThemes();

  // Clear organization cache
  clearOrganizationCache();
};

export const createAdminOrgLoader =
  () =>
  async ({ params }: { params: Record<string, string> }) => {
    const orgSlug = params.orgSlug as string;

    if (!orgSlug) {
      throw new Error("Organization slug is required");
    }

    const organizationInfo = await validateAdminAccess(orgSlug);

    // Auto-switch organization context if needed
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      try {
        const payload = JSON.parse(atob(authToken.split(".")[1]));
        const currentOrgId = payload.organization_id;

        if (currentOrgId !== organizationInfo.organizationId) {
          console.log("Organization context mismatch - attempting to switch for admin access", {
            currentOrgId,
            requestedOrgId: organizationInfo.organizationId,
          });

          // Need to switch organization context
          const response = await fetch(buildApiUrl('/auth/switch-organization'), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              organizationId: organizationInfo.organizationId,
            }),
          });

          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data?.token) {
              // Update token in localStorage
              localStorage.setItem("authToken", result.data.token);
              if (result.data.refreshToken) {
                localStorage.setItem("refreshToken", result.data.refreshToken);
              }
              console.log(
                "Successfully switched organization context for admin access - refreshing page"
              );

              // Reload the page to update AuthContext with new organization data
              window.location.reload();
              return; // Return early as page is reloading
            }
          } else {
            // If switch fails, user might not have access to this org as admin
            throw new Error(
              "You do not have admin access to this organization"
            );
          }
        }
      } catch (error: unknown) {
        console.error("Error during admin organization context switch:", error);
        if (error instanceof Error && error.message?.includes("admin")) {
          throw error;
        }
        throw new Error("You do not have admin access to this organization");
      }
    }

    return {
      organizationInfo,
      orgSlug,
      isPathBased: true,
    } as PortalLoaderData;
  };
